import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { api } from "./api";
import { VIEWBOX, STATUS_TONE, tokenSpec, buildNodes, arcPath, diffTransactions } from "./liveMapLogic";
import { LAND_PATH, BORDERS_PATH } from "./liveMapGeo";
import { mergeRecent, unionCodes, capSeen, pickDemoTransfer, demoDelay, nextCursor, shouldRebaseline } from "./liveMapState";

// OPS-32: mapa de transacciones en vivo del tab Negocio. Se monta solo con la
// Section expandida; al colapsar se desmonta y el cleanup corta el polling.

const POLL_MS = 4000;
const REQUEST_TIMEOUT_MS = 15000;
const TRAVEL_MS = 1800;
const STAGGER_MS = 40;
const HOLD_MS = 900;
const FADE_OUT_MS = 250;
const REDUCED_FADE_MS = 200;
const REDUCED_LIFETIME_MS = 2000;
const EFFECT_MS = 280;
const SHAKE_MS = 250;
// Solapamiento del cursor, ver nextCursor() en liveMapState.js.
const CURSOR_OVERLAP_MS = 5000;
const REBASELINE_AFTER_HIDDEN_MS = 30000;
const FRESH_SPACING_MS = 150;
const MAX_PARTICLES = 24;
const MAX_EFFECTS = 12;
const MAX_SEEN = 5000;
const RECENT_LIMIT = 5;
const EASE_SPLINE = "0.23 1 0.32 1";
const EASE_CSS = "cubic-bezier(0.23, 1, 0.32, 1)";
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";
// Solo para el modo demo cuando todavia no hay paises reales cargados.
const DEMO_FALLBACK_CODES = ["US", "MX", "CO", "BR", "CL", "AR", "UY"];

// Posicion de la etiqueta segun el lado indicado en liveMapGeo.js, para que
// vecinos cercanos (Centroamerica, Caribe) no se pisen.
const LABEL_LAYOUT = {
  b: { x: 0, y: 24, anchor: "middle" },
  t: { x: 0, y: -14, anchor: "middle" },
  l: { x: -12, y: 4, anchor: "end" },
  r: { x: 12, y: 4, anchor: "start" },
};

// Fondo geografico estatico (Natural Earth 1:50m). Va primero en el SVG y sin
// eventos de puntero; es un elemento constante, React no lo vuelve a reconciliar.
const GEO_BACKGROUND = (
  <g aria-hidden="true" style={{ pointerEvents: "none" }}>
    <path d={LAND_PATH} fill="#0f1a0f" stroke="#1a2a1a" strokeWidth="0.8" strokeLinejoin="round" />
    <path d={BORDERS_PATH} fill="none" stroke="#1a2a1a" strokeWidth="0.6" strokeLinejoin="round" />
  </g>
);

const TONE_COLORS = { moving: "#a3e635", waiting: "#fbbf24", done: "#34d399", bad: "#f87171" };

// Copia de las etiquetas de STATUS_STYLES (txStatus.js), solo los estados que
// devuelve el endpoint (LIVE_MAP_STATUSES de dashboard.py).
const STATUS_BADGES = {
  executing:        { bg: "#1a1f0d", text: "#a3e635", label: "Ejecutando" },
  processing:       { bg: "#1a1f0d", text: "#a3e635", label: "Procesando" },
  awaiting_funding: { bg: "#1a1a1a", text: "#c4b5fd", label: "Esperando fondos" },
  pending_claim:    { bg: "#1a1400", text: "#fbbf24", label: "Pendiente de reclamo" },
  completed:        { bg: "#0d2b1a", text: "#34d399", label: "Completada" },
  failed:           { bg: "#2b0d0d", text: "#f87171", label: "Fallida" },
  reversed:         { bg: "#1a1a1a", text: "#fca5a5", label: "Revertida" },
};

const KEYFRAMES = `
@keyframes ltm-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes ltm-out { from { opacity: 1 } to { opacity: 0 } }
@keyframes ltm-fade { from { opacity: 0.9 } to { opacity: 0 } }
@keyframes ltm-pulse { from { transform: scale(1); opacity: 0.9 } to { transform: scale(2.6); opacity: 0 } }
@keyframes ltm-shake {
  0%, 100% { transform: translateX(0) }
  20% { transform: translateX(-4px) }
  40% { transform: translateX(4px) }
  60% { transform: translateX(-3px) }
  80% { transform: translateX(3px) }
}
`;

const toneOf = (status) => STATUS_TONE[status] || "moving";
const isFinalTone = (tone) => tone === "done" || tone === "bad";
const nodeMap = (codes) => new Map(buildNodes([...codes]).map((n) => [n.code, n]));
const fmtAmount = (tx) =>
  `${Number(tx.amount_usd || 0).toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${tx.unit || "USD"}`;

const readReducedMotion = () => {
  try {
    return !!(window.matchMedia && window.matchMedia(REDUCED_QUERY).matches);
  } catch {
    return false;
  }
};

function TokenShape({ type }) {
  if (type === "bill") {
    return (
      <g>
        <rect x="-8" y="-4.5" width="16" height="9" rx="1" fill="#14532d" stroke="#22c55e" strokeWidth="0.8" />
        <line x1="-5" y1="0" x2="5" y2="0" stroke="#86efac" strokeWidth="0.8" />
      </g>
    );
  }
  if (type === "chip") {
    return <circle r="5.5" fill="#1e1530" stroke="#a78bfa" strokeWidth="1.4" strokeDasharray="2 1.6" />;
  }
  return <circle r="4.5" fill="#e3b341" stroke="#8a6a1a" strokeWidth="1" />;
}

function TokenIcon({ type }) {
  return (
    <svg width="18" height="14" viewBox="-9 -7 18 14" style={{ flexShrink: 0 }}>
      <TokenShape type={type} />
    </svg>
  );
}

// Una unidad viajando por el arco. begin="indefinite" + beginElementAt: un
// elemento SMIL insertado despues de cargar el SVG con begin="0s" se toma como
// ya terminado (el reloj del documento corre desde el montaje del <svg>) y
// apareceria congelado en el destino.
function TokenUnit({ path, delayMs, type, ringColor }) {
  const motionRef = useRef(null);
  const fadeRef = useRef(null);
  useEffect(() => {
    const offset = delayMs / 1000;
    for (const el of [motionRef.current, fadeRef.current]) {
      if (el && typeof el.beginElementAt === "function") el.beginElementAt(offset);
    }
  }, [delayMs]);
  return (
    <g opacity="0">
      <animate ref={fadeRef} attributeName="opacity" from="0" to="1" dur="0.12s" begin="indefinite" fill="freeze" />
      <animateMotion ref={motionRef} path={path} dur={`${TRAVEL_MS / 1000}s`} begin="indefinite" fill="freeze"
        keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines={EASE_SPLINE} />
      <TokenShape type={type} />
      {ringColor && <circle r="10" fill="none" stroke={ringColor} strokeOpacity="0.8" strokeWidth="1.2" />}
    </g>
  );
}

function Particle({ p }) {
  const color = TONE_COLORS[p.arrived ? toneOf(p.status) : "moving"];
  const out = `ltm-out ${FADE_OUT_MS}ms ease-in ${p.lifetime - FADE_OUT_MS}ms forwards`;
  const units = Array.from({ length: p.token.count }, (_, i) => i);
  if (p.reduced) {
    return (
      <g transform={`translate(${p.to.x} ${p.to.y})`}
        style={{ animation: `ltm-in ${REDUCED_FADE_MS}ms ease-out both, ${out}` }}>
        {units.map((i) => (
          <g key={i} transform={`translate(${i * 3} ${-i * 2})`}><TokenShape type={p.token.type} /></g>
        ))}
        <circle r="10" fill="none" stroke={color} strokeOpacity="0.8" strokeWidth="1.2" />
      </g>
    );
  }
  return (
    <g style={{ animation: out }}>
      {units.map((i) => (
        <TokenUnit key={i} path={p.path} delayMs={i * STAGGER_MS} type={p.token.type} ringColor={i === 0 ? color : null} />
      ))}
    </g>
  );
}

function NodeEffect({ e, reduced }) {
  const color = TONE_COLORS[e.tone];
  if (e.tone === "bad") {
    return (
      <g transform={`translate(${e.x} ${e.y})`}>
        <circle r="11" fill="none" stroke={color} strokeWidth="1.5"
          style={{ animation: reduced ? `ltm-fade ${EFFECT_MS}ms ease-out forwards` : `ltm-shake ${SHAKE_MS}ms ${EASE_CSS}, ltm-fade ${EFFECT_MS}ms ease-out forwards` }} />
      </g>
    );
  }
  return (
    <circle cx={e.x} cy={e.y} r="10" fill="none" stroke={color} strokeWidth="1.5"
      style={{ transformBox: "fill-box", transformOrigin: "center",
        animation: reduced ? `ltm-fade ${EFFECT_MS}ms ease-out forwards` : `ltm-pulse ${EFFECT_MS}ms ${EASE_CSS} forwards` }} />
  );
}

export default function LiveTransactionMap() {
  const [codes, setCodes] = useState(() => new Set());
  const [particles, setParticles] = useState([]);
  const [effects, setEffects] = useState([]);
  const [recent, setRecent] = useState([]);
  const [demo, setDemo] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [reduced, setReduced] = useState(readReducedMotion);

  const codesRef = useRef(codes);
  const seenRef = useRef(null); // null = sin linea base todavia
  const sinceRef = useRef(null);
  const timersRef = useRef(new Set());
  const aliveRef = useRef(new Map()); // txId -> key de su particula viva
  const reducedRef = useRef(reduced);
  const seqRef = useRef(0);

  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      fn();
    }, ms);
    timersRef.current.add(id);
  }, []);

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) clearTimeout(id);
    timersRef.current.clear();
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    let mq;
    try {
      mq = window.matchMedia(REDUCED_QUERY);
    } catch {
      return undefined;
    }
    const onChange = () => {
      reducedRef.current = mq.matches;
      setReduced(mq.matches);
    };
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const addCodes = useCallback((countries, txs) => {
    const next = unionCodes(codesRef.current, countries, txs);
    if (next !== codesRef.current) {
      codesRef.current = next;
      setCodes(next);
    }
  }, []);

  const spawn = useCallback((tx, positions) => {
    const from = positions.get(tx.source_country);
    const to = positions.get(tx.target_country);
    if (!from || !to) return;
    seqRef.current += 1;
    const key = `p${seqRef.current}`;
    const token = tokenSpec(tx.amount_usd, tx.kind);
    const reducedNow = reducedRef.current;
    const travel = TRAVEL_MS + (token.count - 1) * STAGGER_MS;
    const lifetime = reducedNow ? REDUCED_LIFETIME_MS : travel + HOLD_MS;
    const particle = {
      key, txId: tx.id, path: arcPath(from, to), to: { x: to.x, y: to.y }, token,
      status: tx.status, arrived: reducedNow, reduced: reducedNow, lifetime,
    };
    aliveRef.current.set(tx.id, key);
    setParticles((ps) => {
      const next = [...ps, particle];
      return next.length > MAX_PARTICLES ? next.slice(next.length - MAX_PARTICLES) : next;
    });
    if (!reducedNow) {
      schedule(() => setParticles((ps) => ps.map((p) => (p.key === key ? { ...p, arrived: true } : p))), TRAVEL_MS);
    }
    schedule(() => {
      if (aliveRef.current.get(tx.id) === key) aliveRef.current.delete(tx.id);
      setParticles((ps) => ps.filter((p) => p.key !== key));
    }, lifetime);
  }, [schedule]);

  // Cambio de estado de una transferencia ya vista: si su particula sigue
  // viva cambia de tono; si ya termino el viaje, pulso (completed) o sacudon
  // (failed/reversed) en el nodo destino.
  const applyChange = useCallback((tx, positions) => {
    const key = aliveRef.current.get(tx.id);
    if (key) {
      setParticles((ps) => ps.map((p) => (p.key === key ? { ...p, status: tx.status } : p)));
      return;
    }
    const tone = toneOf(tx.status);
    const at = positions.get(tx.target_country);
    if (!isFinalTone(tone) || !at) return;
    seqRef.current += 1;
    const ekey = `e${seqRef.current}`;
    setEffects((es) => [...es.slice(-(MAX_EFFECTS - 1)), { key: ekey, x: at.x, y: at.y, tone }]);
    schedule(() => setEffects((es) => es.filter((e) => e.key !== ekey)), EFFECT_MS);
  }, [schedule]);

  const handleData = useCallback((data) => {
    const txs = Array.isArray(data.transactions) ? data.transactions : [];
    addCodes(data.countries, txs);
    sinceRef.current = nextCursor(sinceRef.current, data.server_time, CURSOR_OVERLAP_MS);
    setRecent((prev) => mergeRecent(prev, txs, RECENT_LIMIT));
    setLoaded(true);
    if (seenRef.current === null) {
      // Primer poll: linea base, sin particulas (Review Focus 7).
      seenRef.current = capSeen(diffTransactions(new Map(), txs).next, MAX_SEEN);
      return;
    }
    const { fresh, changed, next } = diffTransactions(seenRef.current, txs);
    seenRef.current = capSeen(next, MAX_SEEN);
    const positions = nodeMap(codesRef.current);
    // El server ordena desc; se lanzan de la mas vieja a la mas nueva.
    fresh.slice().reverse().forEach((tx, i) => {
      if (i === 0) spawn(tx, positions);
      else schedule(() => spawn(tx, positions), i * FRESH_SPACING_MS);
    });
    changed.forEach((tx) => applyChange(tx, positions));
  }, [addCodes, spawn, applyChange, schedule]);

  // Polling real (Review Focus 6 y 8).
  useEffect(() => {
    if (demo) return undefined;
    let cancelled = false;
    let controller = null;
    let startedAt = 0;
    const load = () => {
      if (cancelled || document.hidden) return;
      if (controller) {
        // Sin requests solapados; uno colgado se aborta pasado el timeout.
        if (Date.now() - startedAt < REQUEST_TIMEOUT_MS) return;
        controller.abort();
        controller = null;
      }
      const c = new AbortController();
      controller = c;
      startedAt = Date.now();
      api.getLiveTransactions(sinceRef.current, c.signal)
        .then((data) => {
          if (cancelled || c.signal.aborted || !data) return;
          setError(null);
          handleData(data);
        })
        .catch((e) => {
          if (cancelled || c.signal.aborted || (e && e.name === "AbortError")) return;
          setError("No se pudo actualizar el mapa");
        })
        .finally(() => {
          if (controller === c) controller = null;
        });
    };
    load();
    const interval = setInterval(load, POLL_MS);
    let hiddenAt = document.hidden ? Date.now() : null;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      // Tras mucho tiempo oculta, nueva linea base silenciosa en vez de
      // reproducir hasta 50 filas como viajes.
      if (hiddenAt !== null && shouldRebaseline(Date.now() - hiddenAt, REBASELINE_AFTER_HIDDEN_MS)) {
        if (controller) controller.abort();
        controller = null;
        seenRef.current = null;
        sinceRef.current = null;
      }
      hiddenAt = null;
      load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      if (controller) controller.abort();
    };
  }, [demo, handleData]);

  // Modo demo: 100% del lado del cliente, nunca llama al endpoint.
  useEffect(() => {
    if (!demo) return undefined;
    let seq = 0;
    let timer = null;
    // Con la pestana oculta no se genera nada (ni timers ni estado ni SMIL):
    // tick se corta y visibilitychange lo relanza al volver.
    const tick = () => {
      if (document.hidden) {
        timer = null;
        return;
      }
      const demoCodes = codesRef.current.size >= 2 ? [...codesRef.current] : DEMO_FALLBACK_CODES;
      const positions = nodeMap(demoCodes);
      seq += 1;
      const pick = pickDemoTransfer(demoCodes, seq);
      if (pick) {
        setRecent((prev) => mergeRecent(prev, [pick.tx], RECENT_LIMIT));
        spawn(pick.tx, positions);
        schedule(() => {
          const resolved = { ...pick.tx, status: pick.finalStatus };
          setRecent((prev) => prev.map((t) => (t.id === resolved.id ? resolved : t)));
          applyChange(resolved, positions);
        }, TRAVEL_MS);
      }
      timer = setTimeout(tick, demoDelay());
    };
    const onVisibility = () => {
      if (!document.hidden && timer === null) tick();
    };
    document.addEventListener("visibilitychange", onVisibility);
    tick();
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [demo, spawn, applyChange, schedule]);

  // Al entrar o salir de demo no se mezcla nada: particulas, recientes, linea
  // base y cursor arrancan de cero.
  const toggleDemo = () => {
    clearTimers();
    aliveRef.current.clear();
    seenRef.current = null;
    sinceRef.current = null;
    setParticles([]);
    setEffects([]);
    setRecent([]);
    setError(null);
    setLoaded(false);
    setDemo((d) => !d);
  };

  // Mismo criterio que spawn(): en demo sin paises reales, layout de respaldo.
  const layoutKey = (demo && codes.size < 2 ? DEMO_FALLBACK_CODES : [...codes]).join(",");
  const nodes = useMemo(() => buildNodes(layoutKey ? layoutKey.split(",") : []), [layoutKey]);
  const positions = useMemo(() => new Map(nodes.map((n) => [n.code, n])), [nodes]);

  const routes = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const tx of recent) {
      const id = `${tx.source_country}-${tx.target_country}`;
      const from = positions.get(tx.source_country);
      const to = positions.get(tx.target_country);
      if (seen.has(id) || !from || !to) continue;
      seen.add(id);
      out.push({ id, d: arcPath(from, to) });
    }
    return out;
  }, [recent, positions]);

  const activeCodes = new Set(recent.flatMap((tx) => [tx.source_country, tx.target_country]));

  return (
    <div style={{ background: "#080c08", border: "1px solid #1a2a1a", borderRadius: 2, padding: 16, color: "#c8e6c8", fontFamily: "'DM Mono', monospace" }}>
      <style>{KEYFRAMES}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 10, color: error ? "#f87171" : "#4a6a4a", letterSpacing: 1 }}>
          {demo ? "Datos simulados, no son transferencias reales" : error || `Actualiza cada ${POLL_MS / 1000} s`}
        </div>
        {demo && (
          <span style={{ fontSize: 9, letterSpacing: 2, color: "#fbbf24", border: "1px solid #fbbf24", borderRadius: 2, padding: "2px 6px" }}>DEMO</span>
        )}
        <button onClick={toggleDemo}
          style={{ marginLeft: "auto", padding: "6px 16px", fontSize: 11, letterSpacing: 1, background: demo ? "#1a3a1a" : "transparent", border: `1px solid ${demo ? "#34d399" : "#1a2a1a"}`, borderRadius: 2, color: demo ? "#34d399" : "#4a6a4a", cursor: "pointer", fontFamily: "inherit" }}>
          {demo ? "Salir de demo" : "Demo"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 480px", minWidth: 0 }}>
          <svg viewBox={`0 0 ${VIEWBOX.w} ${VIEWBOX.h}`} style={{ width: "100%", height: "auto", maxHeight: "85vh", display: "block" }}
            role="img" aria-label="Mapa de transferencias entre paises">
            {GEO_BACKGROUND}
            {routes.map((r) => (
              <path key={r.id} d={r.d} fill="none" stroke="#2a4a2a" strokeWidth="1" strokeDasharray="3 5" />
            ))}
            {nodes.map((n) => {
              const active = activeCodes.has(n.code);
              const lab = LABEL_LAYOUT[n.label] || LABEL_LAYOUT.b;
              return (
                <g key={n.code} transform={`translate(${n.x} ${n.y})`}>
                  <circle r="7" fill="#0d120d" stroke={active ? "#34d399" : "#2a4a2a"} strokeWidth="1.2" />
                  <text x={lab.x} y={lab.y} textAnchor={lab.anchor} fontSize="13" letterSpacing="2" fill={active ? "#c8e6c8" : "#4a6a4a"}
                    style={{ fontFamily: "'DM Mono', monospace" }}>{n.code}</text>
                </g>
              );
            })}
            {effects.map((e) => <NodeEffect key={e.key} e={e} reduced={reduced} />)}
            {particles.map((p) => <Particle key={p.key} p={p} />)}
          </svg>
        </div>

        <div style={{ flex: "0 1 280px", minWidth: 220 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", marginBottom: 10 }}>Ultimas transferencias</div>
          {recent.length === 0 ? (
            <div style={{ fontSize: 12, color: "#3a5a3a", padding: "12px 0" }}>
              {demo || loaded ? "Sin transferencias en los ultimos 10 minutos" : error || "Cargando..."}
            </div>
          ) : (
            recent.map((tx) => {
              const badge = STATUS_BADGES[tx.status] || { bg: "#111811", text: "#9ca3af", label: tx.status };
              const token = tokenSpec(tx.amount_usd, tx.kind);
              return (
                <div key={tx.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #111811", fontSize: 12 }}>
                  <TokenIcon type={token.type} />
                  <span style={{ color: "#c8e6c8", whiteSpace: "nowrap" }}>{tx.source_country} -&gt; {tx.target_country}</span>
                  <span style={{ color: "#6b9a6b", whiteSpace: "nowrap", marginLeft: "auto" }}>{fmtAmount(tx)}</span>
                  <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 2, background: badge.bg, color: badge.text, whiteSpace: "nowrap" }}>{badge.label}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
